import React from 'react'
import { Fragment } from 'react'

import { TableSectionProps, ISection } from './TableSection'

export interface TableData {
    [prop: string]: any
}

type TableColumnResponsiveType = 'sm' | 'md' | 'lg' | 'xl';

export interface TableColumn<D> {
    name: string
    actionColumn?: boolean
    content: { (row: D, i: number): any }
    responsive?: TableColumnResponsiveType
    printOnly?: boolean
    title?: string
    width?: string
}

export interface TableBodyProps<D extends TableData> {
    id: string
    value: D[]
    summaryRows?: D[]
    onChange?: { (value: D[]): any }
    columns: TableColumn<D>[]
    renderSection?: { (p: TableSectionProps<D>, i: number): JSX.Element }
    noRowsMessage?: string
    preview?: boolean
    alwaysShowAllRows?: boolean
    rowClickCallback?: { fn: (param: string) => void, param: string}
    additionalTableClasses?: string

    allowEdit?: boolean
}

export interface TableBodyState<D extends TableData> {
    value: D[]
    expanded: D[]
    expandNew: boolean
    windowWidth: number
    rowsDisplayed: boolean
}

export class TableBody<D extends TableData> extends React.Component<TableBodyProps<D>, TableBodyState<D>> {
    sectionRefs: { [key: number]: ISection }
    rindowResizeRef: NodeJS.Timeout

    constructor(props: TableBodyProps<D>, context?: any) {
      super(props, context)

      this.sectionRefs = {}

      this.state = {
        value: props.value,
        expanded: [],
        expandNew: false,
        windowWidth: window.innerWidth,
        rowsDisplayed: props.value && props.value.length <= 10,
      }
    }

    componentDidMount() {
      window.addEventListener('resize', this.onWindowResize)
    }

    componentWillUnmount() {
      window.removeEventListener('resize', this.onWindowResize)
    }

    componentDidUpdate(prevProps: TableBodyProps<D>, prevState: TableBodyState<D>) {
      if (this.props.value != prevProps.value) {
        this.setState({ value: this.props.value })
      }
    }

    onWindowResize = () => {
      setTimeout(() => {
        if (this.state.windowWidth != window.innerWidth) {
          this.setState({ windowWidth: window.innerWidth })
        }
      }, 1000)
    };

    public saveAndClose(callback: { (res: boolean): any }) {
      const saveNext = () => {
        const sectionKeys: number[] = []
        for (const key in this.sectionRefs) {
          sectionKeys.push(parseInt(key))
        }
        sectionKeys.sort((a, b) => {
          if (a > b) {
            return 1
          }
          if (a < b) {
            return -1
          }

          return 0
        })

        if (sectionKeys.length > 0) {
          this.sectionRefs[sectionKeys[0]].saveAndClose(onSavedAndClosed)
        } else {
          callback && callback(true)
        }
      }

      var onSavedAndClosed = (res: boolean) => {
        setTimeout(() => {
          if (!res) {
            callback && callback(false)
          } else {
            saveNext()
          }
        }, 2)
      }

      saveNext()
    }

    public toggleNew(show: boolean = null) {
      if (show === null) {
        show = !this.state.expandNew
      }
      this.setState({ expandNew: show })
    }

    private onChange = (v: D, i: number, close?: boolean) => {
      const value = this.props.value.map((r) => r)
      if (i < 0) {
        value.push(v)
        this.setState({ value, expandNew: close ? false : this.state.expandNew }, () => {
          this.props.onChange(value)
        })
      } else {
        value.splice(i, 1, v)

        const expanded = this.state.expanded.map((r) => r)

        if (close) {
          expanded.splice(expanded.indexOf(this.props.value[i]), 1)
        } else {
          expanded.splice(expanded.indexOf(this.props.value[i]), 1, v)
        }

        this.setState({ value, expanded }, () => {
          this.props.onChange(value)
        })
      }
    }

    private onRemove = (i: number) => {
      if (!confirm('Are you sure you want to delete this row?')) {
        return
      }

      if (i < 0) {
        this.setState({ expandNew: false })
        return
      }

      const value = this.props.value.map((r) => r)
      value.splice(i, 1)

      const expanded = this.state.expanded.map((r) => r)
      expanded.splice(expanded.indexOf(this.props.value[i]), 1)

      this.setState({ value, expanded }, () => {
        this.props.onChange(value)
      })
    }

    public deleteAll = () => {
      if (confirm('Are you sure you want to delete all rows from this schedule?')) {
        const value: D[] = []
        this.setState({ value }, () => {
          this.props.onChange(value)
        })
      }
    }

    private onClose = (i: number) => {
      if (i < 0) {
        this.setState({ expandNew: false })
        return
      }

      const expanded = this.state.expanded.map((r) => r)
      expanded.splice(expanded.indexOf(this.props.value[i]), 1)

      this.setState({ expanded })
    }

    private toggle = (i: number) => {
      const expanded = this.state.expanded.map((r) => r)

      const existing = expanded.indexOf(this.props.value[i])
      if (existing >= 0) {
        expanded.splice(existing, 1)
      } else {
        expanded.push(this.props.value[i])
      }

      this.setState({ expanded })
    }

    private toggleRowDisplay = () => {
      this.setState({ rowsDisplayed: !this.state.rowsDisplayed })
    }

    isColumnVisible = (responsive: TableColumnResponsiveType) => {
      if (!responsive) {
        return true
      }

      const types = ['sm', 'md', 'lg', 'xl']
      const widths = [576, 768, 992, 1200]

      const index = types.indexOf(responsive)
      if (index >= 0) {
        if (this.state.windowWidth < widths[index]) {
          return false
        }
      }

      return true
    }

    render() {
      if (!this.props.columns) {
        return <div />
      }

      const columns = this.props.columns.filter((r) => this.isColumnVisible(r.responsive))

      if (this.props.renderSection) {
        if (this.props.allowEdit) {
          columns.push({
            name: '',
            actionColumn: true,
            content: (d, i) => <div style={{ textAlign: 'right' }}><a href="#" title="Delete" onClick={(e) => { this.onRemove(i); e.preventDefault() }}><span className="far fa-trash-alt" style={{ fontSize: '18px', color: 'red' }} /></a></div>,
          })
        }
        columns.push({
          name: '',
          actionColumn: true,
          content: (d, i) => <div style={{ textAlign: 'right' }}><a href="#" title="Expand/Collapse" onClick={(e) => { this.toggle(i); e.preventDefault() }}><img src="/images/expand.png" /></a></div>,
        })
      }

      const heading: JSX.Element[] = columns.map((c, i) => (
        <th title={c.title || ''} key={i} className={c.printOnly ? 'print-only' : ''}>
          {c.name}
        </th>
      ))

      let dataRows: JSX.Element[] = []
      if (this.props.value) {
        dataRows = this.props.value.map((d, i) => {
          let section: JSX.Element
          if (this.state.expanded.indexOf(d) >= 0 && this.props.renderSection) {
            section = (
              <tr key="edit" style={{ backgroundColor: 'whitesmoke' }}>
                <td colSpan={heading.length}>
                  {this.props.renderSection({
                    ref: (r) => (r ? this.sectionRefs[i] = r : delete this.sectionRefs[i]),
                    id: `${this.props.id}-${i}`,
                    model: d,
                    tableModel: this.state.value,
                    onChange: (v, c) => this.onChange(v, i, c),
                    onRemove: () => this.onRemove(i),
                    onClose: () => this.onClose(i),
                    importing: false,
                    preview: this.props.preview,
                  }, i)}
                </td>
              </tr>
            )
          }
          return (
            <Fragment key={i}>
              <tr style={{ cursor: `${this.props.rowClickCallback ? 'pointer' : 'auto'}` }} onClick={() => (this.props.rowClickCallback ? this.props.rowClickCallback.fn(d[this.props.rowClickCallback.param]) : null)}>
                {columns.map((c, j) => (
                  <td key={`${i}-${j}`} style={{width: c.width}} className={c.printOnly ? 'print-only' : ''}>
                    {c.content(d, i)}
                  </td>
                ))}
              </tr>
              {section}
            </Fragment>
          )
        })
      }

      let summaryRows: JSX.Element[] = []
      if (this.props.summaryRows && this.props.summaryRows.length > 0) {
        summaryRows = this.props.summaryRows.map((d, i) => (
          <Fragment key={`summary-${i}`}>
            <tr className="table-summary-top table-summary-bottom">
              {columns.map((c, j) => (
                <td key={`summary-${i}-${j}`}>
                  {c.actionColumn ? '' : c.content(d, i)}
                </td>
              ))}
            </tr>
          </Fragment>
        ))
      }

      dataRows.push(...summaryRows)

      let noDataRow: JSX.Element
      noDataRow = (
        <tr key="noDataRow">
          <td colSpan={heading.length}>
            {this.props.noRowsMessage || 'No data'}
          </td>
        </tr>
      )

      let showHideDataRow: JSX.Element
      showHideDataRow = (
        <tr key="showHideDataRow">
          <td colSpan={heading.length}>
            {dataRows.length > 10 ? `There are ${dataRows.length} rows of data.` : ''}
            {' '}
            <a href="#" onClick={(e) => { this.toggleRowDisplay(); e.preventDefault() }}>{this.state.rowsDisplayed ? 'Hide all rows' : 'Show all rows'}</a>
          </td>
        </tr>
      )

      let rows: JSX.Element[] = []
      if (dataRows.length > 0) {
        if (this.state.rowsDisplayed || this.props.alwaysShowAllRows || this.props.preview) {
          rows = dataRows.map((r) => r)
        }

        if (!this.props.alwaysShowAllRows && !this.props.preview) rows.push(showHideDataRow)
      } else {
        rows.push(noDataRow)
      }

      let section: JSX.Element
      if (this.state.expandNew) {
        section = (
          <tr key="new" style={{ backgroundColor: 'whitesmoke' }}>
            <td colSpan={heading.length}>
              {this.props.renderSection({
                ref: (r) => (r ? this.sectionRefs[-1] = r : delete this.sectionRefs[-1]),
                id: `${this.props.id}-new`,
                model: null,
                tableModel: this.state.value,
                onChange: (v, c) => this.onChange(v, -1, c),
                onRemove: () => this.onRemove(-1),
                onClose: () => this.onClose(-1),
                importing: false,
                preview: this.props.preview,
              }, -1)}
            </td>
          </tr>
        )
      }

    return (
      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        <table className={`table m-0${(this.props.additionalTableClasses ? ` ${this.props.additionalTableClasses}` : '')}`}>
            <thead>
            {section}
            <tr>
                {heading}
            </tr>
            </thead>
            <tbody>
            {rows}
            </tbody>
        </table>
      </div>
    )
  }
}
